// src/components/AddExpenseDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  Card
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { Expense, ExpenseCategory } from '../types';
import { addExpense, updateExpense, getAllExpenseCategories } from '../services/dataService';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onShowSnackbar: (message: string, severity?: 'success' | 'error') => void;
  editData?: Expense;
}

function AddExpenseDialog({ open, onClose, onSuccess, onShowSnackbar, editData }: Props) {
  const [date, setDate] = useState('');
  const [category, setCategory] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (editData) {
      setDate(editData.date);
      setCategory(editData.category);
      setAmount(editData.amount.toString());
      setDescription(editData.description);
    } else {
      setDate(new Date().toISOString().split('T')[0]);
      setCategory('');
      setAmount('');
      setDescription('');
    }
  }, [editData, open]);

  const loadCategories = async () => {
    try {
      const data = await getAllExpenseCategories();
      setCategories(data.filter(c => c.isActive));
    } catch (error) {
      console.error('カテゴリ読み込みエラー:', error);
    }
  };

  const handleSubmit = async () => {
    if (!date || !category || !amount) {
      onShowSnackbar('必須項目を入力してください', 'error');
      return;
    }

    const amountNum = parseInt(amount);
    if (isNaN(amountNum) || amountNum < 0) {
      onShowSnackbar('正しい金額を入力してください', 'error');
      return;
    }

    setLoading(true);

    try {
      const expenseData: Omit<Expense, 'id'> = {
        date,
        category,
        amount: amountNum,
        description,
        createdAt: Date.now()
      };

      if (editData && editData.id) {
        await updateExpense(editData.id, expenseData);
        onShowSnackbar('支出を更新しました');
      } else {
        await addExpense(expenseData);
        onShowSnackbar('支出を追加しました');
      }

      onSuccess();
      handleClose();
    } catch (error) {
      onShowSnackbar('保存に失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDate('');
    setCategory('');
    setAmount('');
    setDescription('');
    onClose();
  };

  const selectedCategoryData = categories.find(c => c.name === category);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { bgcolor: '#1a1a1a', color: 'white', borderRadius: '20px' } }}>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">
            {editData ? '支出を編集' : '支出を追加'}
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: '#888' }}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* 日付 */}
          <TextField
            label="📅 日付"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiInputBase-root': { color: 'white', bgcolor: '#2a2a2a', borderRadius: '12px' },
              '& .MuiInputLabel-root': { color: '#888' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#EF4444' }
            }}
          />

          {/* カテゴリ選択 */}
          <Box>
            <Typography fontSize={16} color="#888" mb={1}>
              🏷️ カテゴリ
            </Typography>
            
            {categories.length === 0 ? (
              <Card sx={{ bgcolor: '#2a2a2a', p: 2, borderRadius: '12px', textAlign: 'center' }}>
                <Typography fontSize={15} color="#888">
                  カテゴリがありません
                </Typography>
                <Typography fontSize={14} color="#666" mt={0.5}>
                  設定画面でカテゴリを追加してください
                </Typography>
              </Card>
            ) : (
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: 1,
                maxHeight: 300,
                overflowY: 'auto',
                p: 0.5
              }}>
                {categories.map((cat) => (
                  <Card
                    key={cat.id || cat.name}
                    onClick={() => setCategory(cat.name)}
                    sx={{
                      bgcolor: category === cat.name ? '#EF4444' : '#2a2a2a',
                      p: 1.5,
                      borderRadius: '12px',
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.2s',
                      border: category === cat.name ? '2px solid #EF4444' : '2px solid transparent',
                      '&:hover': { 
                        bgcolor: category === cat.name ? '#dc2626' : '#333',
                        transform: 'scale(1.05)'
                      }
                    }}
                  >
                    <Typography fontSize={24} mb={0.5}>
                      {cat.icon}
                    </Typography>
                    <Typography 
                      fontSize={13} 
                      fontWeight={category === cat.name ? 'bold' : 'medium'}
                      color={category === cat.name ? 'white' : '#ccc'}
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {cat.name}
                    </Typography>
                  </Card>
                ))}
              </Box>
            )}

            {/* 選択中のカテゴリ表示 */}
            {selectedCategoryData && (
              <Card sx={{ bgcolor: '#0f3d2c', p: 1.5, mt: 1, borderRadius: '10px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography fontSize={20}>{selectedCategoryData.icon}</Typography>
                  <Box>
                    <Typography fontSize={13} color="#888">選択中</Typography>
                    <Typography fontSize={16} fontWeight="bold" color="#10B981">
                      {selectedCategoryData.name}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            )}
          </Box>

          {/* 金額 */}
          <TextField
            label="💰 金額"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            fullWidth
            placeholder="例: 5000"
            sx={{
              '& .MuiInputBase-root': { color: 'white', bgcolor: '#2a2a2a', borderRadius: '12px' },
              '& .MuiInputLabel-root': { color: '#888' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#EF4444' }
            }}
          />

          {/* 説明 */}
          <TextField
            label="📝 説明（任意）"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={3}
            fullWidth
            placeholder="メモや詳細を入力"
            sx={{
              '& .MuiInputBase-root': { color: 'white', bgcolor: '#2a2a2a', borderRadius: '12px' },
              '& .MuiInputLabel-root': { color: '#888' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#EF4444' }
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button 
          onClick={handleClose} 
          sx={{ 
            color: '#888',
            flex: 1,
            height: 48,
            borderRadius: '12px',
            '&:hover': { bgcolor: '#2a2a2a' }
          }}
        >
          キャンセル
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !category}
          sx={{ 
            bgcolor: '#EF4444', 
            flex: 1,
            height: 48,
            borderRadius: '12px',
            fontWeight: 'bold',
            '&:hover': { bgcolor: '#dc2626' },
            '&:disabled': { bgcolor: '#333', color: '#666' }
          }}
        >
          {loading ? '保存中...' : (editData ? '更新' : '追加')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddExpenseDialog;
